// Angular
import { Injectable, inject, signal, WritableSignal } from '@angular/core';
// RxJs
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { catchError, retry, tap } from 'rxjs/operators';
import { Observable, throwError, timer, interval, Subscription } from 'rxjs';
// Service
import { CommonService } from './common.service';
// Constants
import { environment } from '@environment';
import { APP_CONSTANT } from '@constants';

export const WS_ENDPOINT = environment.wsEndpoint;

interface IWebSocketOptions {
  skip_ping?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {

  // Injected services
  private readonly cs = inject(CommonService);

  // Signals
  public isConnected: WritableSignal<boolean> = signal(false);

  // WebSocket
  private socket$: WebSocketSubject<any> | null = null;
  private appSubscription$: Subscription | null = null;

  // Configs
  private readonly RECONNECT_INTERVAL = 3000;
  private readonly MAX_RETRIES = 5;

  // Ping
  private pingInterval$: Subscription | null = null;

  /**
   * Ensure WebSocket connection
   */
  ensureConnected(): void {
    if (this.appSubscription$ && !this.appSubscription$.closed) return;

    this.appSubscription$ = this.connect().subscribe({
      error: (error) => console.error('WebSocket failed after retries:', error),
    });
  }

  /**
   * Connect to WebSocket
   */
  connect(options?: IWebSocketOptions): Observable<any> {
    if (!this.socket$ || this.socket$.closed) {
      this.socket$ = this.getWebSocket(options);
    }

    return this.socket$.pipe(
      tap((msg) => {
        if (!msg?.action || !msg?.event) return;

        // Offline scenarios
        const isOfflineState = (msg.action === 'disconnected' && msg.event === 'user' && msg.response?.device === 'desktop-prime-connect') ||
          (msg.action === 'pong' && msg.event === 'user' && msg.response?.status === 'offline' && msg.response?.device === 'desktop-prime-connect');

        if (isOfflineState) {
          this.isConnected.set(false);
          return;
        }

        // Online scenarios
        const isOnlineState = (msg.action === 'connected' && msg.event === 'user' && msg.response?.device === 'desktop-prime-connect') ||
          (msg.action === 'pong' && msg.event === 'user' && msg.response?.status === 'online' && msg.response?.device === 'desktop-prime-connect');

        if (isOnlineState) {
          this.isConnected.set(true);
        }
      }),
      retry({
        count: this.MAX_RETRIES,
        delay: (error, retryCount) => {
          console.warn(`WebSocket retry attempt #${retryCount}. Error:`, error);
          return timer(this.RECONNECT_INTERVAL);
        }
      }),
      catchError((error) => {
        this.isConnected.set(false);
        this.socket$ = null;
        this.cleanupPing();
        console.error('WebSocket connection failed after retries:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Send message over WebSocket
   */
  sendMessage(message: any): void {
    if (this.socket$ && !this.socket$.closed) {
      this.socket$.next(message);
    } else {
      console.warn('WebSocket is not connected.');
    }
  }

  /**
   * Start ping-pong mechanism
   */
  startPing(): void {
    if (this.pingInterval$) return;

    this.pingInterval$ = interval(10000).subscribe(() => {
      if (this.socket$ && !this.socket$.closed) {
        this.socket$.next({
          event: 'user',
          action: 'ping',
          payload: {
            device: 'desktop-prime-connect',
          },
        });
      } else {
        this.isConnected.set(false);
      }
    });
  }

  /**
   * Cleanup resources and close WebSocket
   */
  close(): void {
    if (this.appSubscription$) {
      this.appSubscription$.unsubscribe();
      this.appSubscription$ = null;
    }

    if (this.socket$) {
      this.socket$.complete();
      this.socket$ = null;
    }

    this.cleanupPing();
    this.isConnected.set(false);
  }

  /**
   * Clear ping timer
   */
  private cleanupPing(): void {
    if (this.pingInterval$) {
      this.pingInterval$.unsubscribe();
      this.pingInterval$ = null;
    }
  }

  /**
   * Create WebSocket subject
   */
  private getWebSocket(options?: IWebSocketOptions): WebSocketSubject<any> {
    const jwt = this.cs.getFromLocalStorage(APP_CONSTANT.JWT_TOKEN_KEY);
    const webSocketUrl = `${WS_ENDPOINT}?token=${jwt}&device=browser-prime`;
    return webSocket({
      url: webSocketUrl,
      openObserver: {
        next: () => {
          if (options?.skip_ping) return;
          this.startPing();
          this.sendMessage({
            event: 'user',
            action: 'ping',
            payload: {
              device: 'desktop-prime-connect',
            },
          });
        }
      },
      closeObserver: {
        next: () => {
          this.isConnected.set(false);
          this.cleanupPing();
          console.warn('WebSocket connection closed');
        }
      }
    });
  }
}